import React, { useState, useEffect } from 'react';
import { normalizeGoogleEventId } from '../../lib/utils/googleEventId';
import {
  Calendar,
  Clock,
  X,
  CalendarClock,
  Phone,
  Mail,
  FileText,
  Video,
  MapPin,
  CheckCircle,
  AlertCircle,
  ClipboardList,
  CalendarCheck,
  User,
  Link
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchReferentiContacts } from '../../lib/utils/fetchReferentiContacts';
import { useAuth } from '../../lib/auth';
import { useGoogleCalendar } from '../../hooks/deals/useGoogleCalendar';
import { useMicrosoftCalendar } from '../../hooks/deals/useMicrosoftCalendar';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useActivityTypesStore, getActivityTypeLabel } from '../../lib/stores/activityTypesStore';
import { Stethoscope } from 'lucide-react';
import { htmlToCalendarText } from '../../lib/utils/calendarText';
import { AlfaDocsService } from '../../lib/services/alfadocsService';
import { AssigneeAvailabilityHint } from '../calendar/team/AssigneeAvailabilityHint';
import { HelpTip } from '../ui/HelpTip';

interface DealActivityFormProps {
  dealId: string;
  activityId?: string;
  editingActivity?: any;
  initialData?: any;
  defaultAssignee?: string; // Default assignee for new activities
  /** Orario di partenza per una nuova attività (es. scelto dalla vista Team) */
  initialStart?: Date;
  onSuccess?: () => void;
  onSave?: (data: any) => void;
  onCancel?: () => void;
  translations?: any;
}

interface ActivityFormData {
  title: string;
  description: string;
  type: 'call' | 'email' | 'task' | 'note' | 'videochiamata' | 'appuntamento' | 'sopralluogo' | 'other' | string;
  status: 'pending' | 'completed' | 'overdue';
  due_date: string;
  due_time: string;
}

interface Attendee {
  email: string;
  name?: string;
  source: 'client' | 'contact' | 'manual' | 'crm_user';
}

// Helper function to parse due_date timestamp into date and time strings
function parseDueDateTime(dueDateString: string | undefined | null): { date: string; time: string } {
  const defaultDate = new Date().toISOString().split('T')[0];
  const defaultTime = '09:00';

  if (!dueDateString) {
    return { date: defaultDate, time: defaultTime };
  }

  try {
    const dueDateTime = new Date(dueDateString);

    // Validate the parsed date
    if (isNaN(dueDateTime.getTime())) {
      console.error('[parseDueDateTime] Invalid date:', dueDateString);
      return { date: defaultDate, time: defaultTime };
    }

    // Extract date in YYYY-MM-DD format (local time)
    const year = dueDateTime.getFullYear();
    const month = String(dueDateTime.getMonth() + 1).padStart(2, '0');
    const day = String(dueDateTime.getDate()).padStart(2, '0');
    const parsedDate = `${year}-${month}-${day}`;

    // Extract time in HH:mm format (local time)
    const hours = String(dueDateTime.getHours()).padStart(2, '0');
    const minutes = String(dueDateTime.getMinutes()).padStart(2, '0');
    const parsedTime = `${hours}:${minutes}`;

    return { date: parsedDate, time: parsedTime };
  } catch (error) {
    console.error('[parseDueDateTime] Error parsing date:', error, dueDateString);
    return { date: defaultDate, time: defaultTime };
  }
}

// Helper function to create enhanced event data with deal/client info and CRM link
function createEnhancedEventData(
  activityData: { title: string; description: string; type?: string },
  dateTime: Date,
  dealData: any,
  dealId: string,
  includeDescriptionInEvent: boolean = false
) {
  const clientName = dealData?.clients?.business_name || 'Cliente';
  const dealTitle = dealData?.title || 'Deal';
  const dealAmount = dealData?.amount;

  const crmUrl = `${window.location.origin}/deals/${dealId}`;

  const endTime = new Date(dateTime.getTime() + 60 * 60 * 1000).toISOString();

  // For videochiamata, appuntamento, sopralluogo: simplified event without CRM details
  if (['videochiamata', 'appuntamento', 'sopralluogo'].includes(activityData.type)) {
    return {
      title: activityData.title, // Simple title without deal name
      description: includeDescriptionInEvent ? htmlToCalendarText(activityData.description) : '', // Only include description if toggle is on
      start_time: dateTime.toISOString(),
      end_time: endTime,
      all_day: false,
      location: '',
      color: '#7986CB',
      // client_id and deal_id for tracking only, no crm_url to avoid backend enhancement
      client_id: dealData?.client_id,
      deal_id: dealId
    };
  }

  // For other activity types: keep the enhanced version with CRM details
  const primaryEmail = dealData?.clients?.email || 'N/D';
  const primaryPhone = dealData?.clients?.phone || 'N/D';
  const contactInfo = dealData?.clients?.contact_name || 'N/D';

  const enhancedDescription = [
    htmlToCalendarText(activityData.description),
    '',
    `--- CRM Info ---`,
    `Deal: ${dealTitle}`,
    ...(dealAmount ? [`Importo: €${Number(dealAmount).toLocaleString('it-IT')}`] : []),
    `Cliente: ${clientName}`,
    `Telefono: ${primaryPhone}`,
    `Email: ${primaryEmail}`,
    '',
    `🔗 Apri nel CRM: ${crmUrl}`,
  ].join('\n');

  return {
    title: `${activityData.title} - ${dealTitle}`,
    description: enhancedDescription,
    start_time: dateTime.toISOString(),
    end_time: endTime,
    all_day: false,
    location: '',
    color: '#7986CB',
    // Additional metadata for backend processing
    client_id: dealData?.client_id,
    deal_id: dealId,
    crm_url: crmUrl
  };
}

export function DealActivityForm({ dealId, activityId, editingActivity, initialData, defaultAssignee, initialStart, onSuccess, onSave, onCancel, translations }: DealActivityFormProps) {
  const { user } = useAuth();
  const { settings } = useSettingsStore();
  const { getEnabledGoogleCalendars, createEvent, updateEvent, deleteEvent } = useGoogleCalendar();
  const {
    createEvent: createMicrosoftEvent,
    updateEvent: updateMicrosoftEvent,
    deleteEvent: deleteMicrosoftEvent
  } = useMicrosoftCalendar();

  // AlfaDocs integration - direct query using user.id
  const [alfadocsActive, setAlfadocsActive] = useState(false);
  const [alfadocsApptConfig, setAlfadocsApptConfig] = useState<any>(null);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();
        if (!profile?.organization_id) return;
        // v1: label/tipi dell'appuntamento clinico dalla connessione attiva più vecchia dell'org
        // (la creazione effettiva passa dai drawer, che risolvono la primaria del cliente).
        const oldestActive = await AlfaDocsService.getOldestActiveConfig(profile.organization_id);
        setAlfadocsActive(!!oldestActive);
        setAlfadocsApptConfig(oldestActive?.configuration?.appointments_config || null);
      } catch {
        setAlfadocsActive(false);
      }
    })();
  }, [user?.id]);
  const alfadocsTypes = (alfadocsApptConfig?.types || [
    { value: 'visita', label: 'Visita studio', enabled: true },
    { value: 'operazione', label: 'Operazione', enabled: true },
    { value: 'telehealth', label: 'Telehealth', enabled: true },
  ]).filter((t: any) => t.enabled);
  const alfadocsLabel = alfadocsApptConfig?.label || 'Appuntamento clinico';

  // Tipi attività custom (per-org) — mostrati in coda alle opzioni di sistema/AlfaDocs
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();
  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);
  const activeCustomTypes = customTypes.filter(t => t.active);

  // Activity settings con fallback per retrocompatibilità
  const activityDefaultType = (settings?.activity_default_type || 'task') as ActivityFormData['type'];
  const activityTitleRequired = settings?.activity_title_required !== false; // true se undefined/null/true
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationUsers, setOrganizationUsers] = useState<Array<{id: string, full_name: string, email: string, google_account_connected?: boolean, google_workspace_scopes?: string[]}>>([]);
  const [formData, setFormData] = useState<ActivityFormData>({
    title: '',
    description: '',
    type: 'task',
    status: 'pending',
    due_date: new Date().toISOString().split('T')[0],
    due_time: '09:00'
  });
  // Se si sta modificando un'attività storica il cui tipo è un custom type nel frattempo
  // disattivato dall'admin, aggiungilo in coda alla lista (non selezionabile per nuove
  // creazioni, ma presente finché resta il valore corrente) per evitare che la select
  // mostri una selezione vuota/mismatch rispetto al valore reale in formData.type.
  const selectedInactiveCustomType = customTypes.find(t => !t.active && t.slug === formData.type);
  const customTypeOptions = selectedInactiveCustomType
    ? [...activeCustomTypes, selectedInactiveCustomType]
    : activeCustomTypes;

  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [previousAssignedTo, setPreviousAssignedTo] = useState<string | null>(null); // Track previous assignee for calendar move
  const [googleEventId, setGoogleEventId] = useState<string | null>(null);
  const [previousCalendarId, setPreviousCalendarId] = useState<string | null>(null); // Track previous calendar

  // User sync settings (loaded from user_profiles)
  const [shouldSyncToGoogleCalendar, setShouldSyncToGoogleCalendar] = useState(false);
  const [activitiesCalendarId, setActivitiesCalendarId] = useState<string | null>(null);

  // Microsoft (Outlook) sync settings — speculari a Google, sync indipendente.
  // Lette dal profilo dell'ASSEGNATARIO (target_user_id via edge, F1B).
  const [microsoftSyncEnabled, setMicrosoftSyncEnabled] = useState(false);
  const [microsoftCalendarId, setMicrosoftCalendarId] = useState<string | null>(null);
  const [microsoftEventId, setMicrosoftEventId] = useState<string | null>(null);
  const [assignedGoogleConnected, setAssignedGoogleConnected] = useState(false);
  const [assignedMicrosoftConnected, setAssignedMicrosoftConnected] = useState(false);

  // Provider meeting per videochiamata: 'none' | 'google_meet' | 'microsoft_teams'
  const [meetingProvider, setMeetingProvider] = useState<'none' | 'google_meet' | 'microsoft_teams'>('none');
  const teamsServiceEnabled = !!(settings as any)?.microsoft_sync_teams;

  // Google Meet state for videochiamata type
  const [creatingMeet, setCreatingMeet] = useState(false);
  const [availableContacts, setAvailableContacts] = useState<Attendee[]>([]);
  const [selectedAttendees, setSelectedAttendees] = useState<Attendee[]>([]);
  const [manualEmail, setManualEmail] = useState('');
  const [includeDescriptionInEvent, setIncludeDescriptionInEvent] = useState(false);

  // State for meet organizer selection (who creates the Google Meet)
  const [meetOrganizer, setMeetOrganizer] = useState<'agent' | 'commercial'>('agent');

  // Deal and client data for enhanced event data
  const [dealData, setDealData] = useState<any>(null);

  // Default translations if not provided (Italian)
  const t = translations || {
    activities: {
      addActivity: 'Aggiungi Attività',
      title: 'Titolo',
      activityTitle: 'Titolo attività',
      type: 'Tipo',
      status: 'Stato',
      date: 'Data',
      time: 'Ora',
      description: 'Descrizione',
      descriptionPlaceholder: 'Inserisci i dettagli di questa attività',
      saving: 'Salvataggio...',
      saveActivity: 'Salva Attività',
      call: 'Chiamata',
      videochiamata: 'Videochiamata',
      appuntamento: 'Appuntamento',
      sopralluogo: 'Sopralluogo',
      email: 'Email',
      task: 'Task',
      note: 'Nota',
      other: 'Altro',
      pending: 'In attesa',
      completed: 'Completata',
      overdue: 'Scaduta',
      assignedTo: 'Assegnato a',
      selectUser: 'Seleziona utente'
    },
    statusUpdate: {
      cancel: 'Annulla'
    }
  };

  // Load deal and client data for enhanced event data
  useEffect(() => {
    const loadDealData = async () => {
      if (!dealId) return;

      try {
        const { data, error } = await supabase
          .from('deals')
          .select(`
            *,
            clients:client_id(
              id,
              business_name,
              contact_name,
              email,
              phone,
              sales_rep_id
            )
          `)
          .eq('id', dealId)
          .single();

        if (error) throw error;
        // Referenti (persone) del cliente via client_relationships — sostituisce l'embed client_contacts
        if ((data as any)?.clients?.id) {
          (data as any).clients.client_contacts = await fetchReferentiContacts((data as any).clients.id);
        }
        setDealData(data);
      } catch (error) {
        console.error('Error loading deal data:', error);
      }
    };

    loadDealData();
  }, [dealId]);

  // Auto-populate attendees when type changes to "videochiamata"
  useEffect(() => {
    if (formData.type === 'videochiamata' && dealData) {
      const contacts: Attendee[] = [];

      // Add client's primary email
      if (dealData?.clients?.email) {
        contacts.push({
          email: dealData.clients.email,
          name: dealData.clients.business_name || undefined,
          source: 'client'
        });
      }

      // Add contacts from client_contacts
      if (dealData?.clients?.client_contacts && Array.isArray(dealData.clients.client_contacts)) {
        dealData.clients.client_contacts.forEach((contact: any) => {
          if (contact.email) {
            contacts.push({
              email: contact.email,
              name: contact.name || undefined,
              source: 'contact'
            });
          }
        });
      }

      setAvailableContacts(contacts);
      setSelectedAttendees(contacts); // All selected by default
    } else if (formData.type !== 'videochiamata') {
      // Reset attendees when type changes away from videochiamata
      setAvailableContacts([]);
      setSelectedAttendees([]);
      setManualEmail('');
    }
  }, [formData.type, dealData]);

  // Default provider meeting per "videochiamata": Google Meet se l'assegnatario ha
  // Google collegato (comportamento storico); per utenti SOLO-Microsoft il default
  // resta 'none' (il link Teams è irreversibile, va scelto esplicitamente).
  useEffect(() => {
    if (formData.type !== 'videochiamata') {
      setMeetingProvider('none');
      return;
    }
    setMeetingProvider(assignedGoogleConnected ? 'google_meet' : 'none');
  }, [formData.type, assignedGoogleConnected]);

  // Get user's organization and organization users
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!user) return;

      try {
        // Get organization ID and sync settings
        const { data, error } = await supabase
          .from('user_profiles')
          .select('organization_id, sync_activities, sync_activities_calendar_id')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (!data?.organization_id) return;

        setOrganizationId(data.organization_id);

        // Only set default assignedTo if NOT editing an existing activity
        // This prevents overwriting the actual assignee when editing
        const isEditingExistingActivity = !!(editingActivity || initialData);

        console.log('[DealActivityForm] Organization data loaded', {
          isEditingExistingActivity,
          willSetDefaultAssignee: !isEditingExistingActivity,
          defaultAssignee,
          currentUserId: user.id
        });

        if (!isEditingExistingActivity) {
          // Only for NEW activities: use defaultAssignee if provided, otherwise use current user
          setAssignedTo(defaultAssignee || user.id);
        }

        // Sync settings are loaded by the useEffect on [assignedTo] change
        // Do NOT set them here to avoid race condition with wrong user's calendar

        // Get all users in the organization, excluding superadmin users
        // Include Google Calendar info to determine who can create meets
        const { data: usersData, error: usersError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, google_account_connected, google_workspace_scopes')
          .eq('organization_id', data.organization_id)
          .eq('active', true) // Escludi gli utenti disattivati
          .eq('is_super_admin', false);

        if (usersError) throw usersError;
        if (usersData) setOrganizationUsers(usersData);

      } catch (err) {
        console.error('Error fetching organization data:', err);
        setError('Failed to load user data');
      }
    };

    fetchOrganizationData();
  }, [user, editingActivity, initialData, defaultAssignee]);

  // Ricarica le impostazioni di sync quando cambia l'utente assegnato
  // Questo è l'unico punto che gestisce le sync settings per le attività in modifica
  useEffect(() => {
    const loadAssignedUserSyncSettings = async () => {
      if (!user || !assignedTo) return;

      try {
        // Carica sempre le sync settings dell'utente assegnato (anche se è l'utente corrente)
        // Questo gestisce sia il caso di attività assegnate ad altri che a se stessi
        console.log('[DealActivityForm] Loading sync settings for assigned user:', assignedTo);

        // Google + Microsoft dell'ASSEGNATARIO (una sola query).
        // La edge microsoft-calendar-modify riceve target_user_id = assegnatario.
        const { data: assignedUserData, error: assignedUserError } = await supabase
          .from('user_profiles')
          .select('sync_activities, sync_activities_calendar_id, google_account_connected, microsoft_account_connected, microsoft_sync_activities, microsoft_sync_activities_calendar_id')
          .eq('id', assignedTo)
          .single();

        if (!assignedUserError && assignedUserData) {
          setShouldSyncToGoogleCalendar(assignedUserData.sync_activities || false);
          setActivitiesCalendarId(assignedUserData.sync_activities_calendar_id || null);
          setAssignedGoogleConnected(!!(assignedUserData as any).google_account_connected);
          setAssignedMicrosoftConnected(!!(assignedUserData as any).microsoft_account_connected);
          setMicrosoftSyncEnabled(!!(assignedUserData as any).microsoft_sync_activities);
          setMicrosoftCalendarId((assignedUserData as any).microsoft_sync_activities_calendar_id || null);
          console.log('[DealActivityForm] Loaded assigned user sync settings:', {
            userId: assignedTo,
            syncEnabled: assignedUserData.sync_activities,
            calendarId: assignedUserData.sync_activities_calendar_id
          });
        }
      } catch (err) {
        console.error('Error loading assigned user sync settings:', err);
      }
    };

    loadAssignedUserSyncSettings();
  }, [assignedTo, user]);

  // Precompile form when editing an activity or using initial data
  useEffect(() => {
    const dataToUse = editingActivity || initialData;

    console.log('[DealActivityForm] Form data useEffect triggered', {
      hasEditingActivity: !!editingActivity,
      hasInitialData: !!initialData,
      activityId: dataToUse?.id,
      title: dataToUse?.title,
      due_date: dataToUse?.due_date,
      assigned_to: dataToUse?.assigned_to
    });

    if (dataToUse) {
      // Parse date/time using helper function
      const { date: parsedDate, time: parsedTime } = parseDueDateTime(
        dataToUse.due_date || dataToUse.start_date
      );

      console.log('[DealActivityForm] Parsed date/time:', {
        original: dataToUse.due_date || dataToUse.start_date,
        parsedDate,
        parsedTime
      });

      // Update form data with parsed values
      setFormData({
        title: dataToUse.title || '',
        description: dataToUse.description || dataToUse.notes || '',
        type: dataToUse.type || dataToUse.activity_type || 'task',
        status: dataToUse.status || 'pending',
        due_date: parsedDate,  // Always in YYYY-MM-DD format
        due_time: parsedTime   // Always in HH:mm format
      });

      const assigneeToSet = dataToUse.assigned_to || dataToUse.user_id || null;
      console.log('[DealActivityForm] Setting assignedTo:', {
        from_assigned_to: dataToUse.assigned_to,
        from_user_id: dataToUse.user_id,
        final_value: assigneeToSet
      });

      setAssignedTo(assigneeToSet);
      setPreviousAssignedTo(assigneeToSet); // Save original assignee
      setGoogleEventId(dataToUse.google_event_id || null);
      setMicrosoftEventId(dataToUse.microsoft_event_id || null);
      // We'll load the previous calendar ID when we load sync settings
      if (assigneeToSet) {
        supabase
          .from('user_profiles')
          .select('sync_activities_calendar_id')
          .eq('id', assigneeToSet)
          .single()
          .then(({ data }) => {
            if (data) {
              setPreviousCalendarId(data.sync_activities_calendar_id);
            }
          });
      }
    } else {
      console.log('[DealActivityForm] No editing data, using defaults with activityDefaultType:', activityDefaultType);
      // Per nuova attività, usa il tipo di default dalle settings dell'organizzazione
      const pad = (n: number) => String(n).padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        type: activityDefaultType,
        ...(initialStart ? {
          due_date: `${initialStart.getFullYear()}-${pad(initialStart.getMonth() + 1)}-${pad(initialStart.getDate())}`,
          due_time: `${pad(initialStart.getHours())}:${pad(initialStart.getMinutes())}`
        } : {})
      }));
    }
  }, [editingActivity, initialData, activityDefaultType, initialStart?.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('[DealActivityForm] handleSubmit - Current state:', {
      assignedTo,
      assignedToType: typeof assignedTo,
      assignedToIsEmpty: assignedTo === '',
      assignedToIsNull: assignedTo === null,
      userId: user?.id,
      willUseValue: assignedTo && assignedTo !== '' ? assignedTo : user?.id,
      isEditing: !!(editingActivity || activityId)
    });

    if (!user || !organizationId) {
      setError('User data not available');
      return;
    }

    // Validate title (solo se obbligatorio nelle settings)
    if (!formData.title.trim()) {
      if (activityTitleRequired) {
        setTitleError(true);
        return;
      } else {
        // Auto-popola il titolo con il tipo tradotto
        const typeLabels: Record<string, string> = {
          call: 'Chiamata',
          videochiamata: 'Videochiamata',
          appuntamento: 'Appuntamento',
          sopralluogo: 'Sopralluogo',
          email: 'Email',
          task: 'Task',
          note: 'Nota',
          other: 'Altro'
        };
        // For alfadocs types, use the configured label
        if (formData.type.startsWith('alfadocs_')) {
          const alfaType = alfadocsTypes.find((t: any) => `alfadocs_${t.value}` === formData.type);
          formData.title = alfaType?.label || alfadocsLabel;
        } else {
          formData.title = typeLabels[formData.type] || getActivityTypeLabel(formData.type, customTypes);
        }
      }
    }
    setTitleError(false);

    setLoading(true);
    setError(null);

    try {
      // Combine date and time
      const dateTime = new Date(`${formData.due_date}T${formData.due_time}`);

      console.log(`Creating activity for deal: ${dealId}`);

      // Check if the user_id column exists
      let hasUserProfileColumn = false;
      try {
        const { error: columnCheckError } = await supabase
          .from('sales_activities')
          .select('user_id')
          .limit(1);

        // If there's no error, the column exists
        hasUserProfileColumn = !columnCheckError;
      } catch (e) {
        console.log('Error checking for user_id column:', e);
      }

      let newGoogleEventId = googleEventId;
      let meetLink: string | null = null;

      // Determine the organizer based on the meetOrganizer toggle
      const assignedUserId = (assignedTo && assignedTo !== '') ? assignedTo : null;
      const salesRepId = dealData?.clients?.sales_rep_id;

      // Check if both agent and commercial have Google Calendar
      const agentHasGoogle = organizationUsers.find(u => u.id === assignedUserId)
        ?.google_workspace_scopes?.includes('https://www.googleapis.com/auth/calendar');
      const commercialHasGoogle = salesRepId && organizationUsers.find(u => u.id === salesRepId)
        ?.google_workspace_scopes?.includes('https://www.googleapis.com/auth/calendar');

      // Determine the actual organizer based on the toggle and availability
      let organizerId: string;
      let organizerCalendarId: string | null = activitiesCalendarId;

      if (formData.type === 'videochiamata' && meetOrganizer === 'commercial' && salesRepId && commercialHasGoogle) {
        // Use commercial as organizer
        organizerId = salesRepId;
        // Get the commercial's calendar
        const { data: commercialProfile } = await supabase
          .from('user_profiles')
          .select('sync_activities_calendar_id')
          .eq('id', salesRepId)
          .single();
        organizerCalendarId = commercialProfile?.sync_activities_calendar_id || null;
      } else if (agentHasGoogle && assignedUserId) {
        // Use agent as organizer
        organizerId = assignedUserId;
      } else {
        // Fallback to current user
        organizerId = user.id;
      }

      console.log('[DealActivityForm] Using calendar settings:', {
        organizerId,
        assignedUserId,
        salesRepId,
        meetOrganizer,
        syncEnabled: shouldSyncToGoogleCalendar,
        calendarId: organizerCalendarId
      });

      // Handle Google Calendar sync if enabled for the current user
      if (shouldSyncToGoogleCalendar && activitiesCalendarId) {
        try {
          // Prepare enhanced event data with deal/client info
          const eventData = dealData
            ? createEnhancedEventData(formData, dateTime, dealData, dealId, includeDescriptionInEvent)
            : {
                title: formData.title,
                description: formData.description,
                start_time: dateTime.toISOString(),
                end_time: new Date(dateTime.getTime() + 60 * 60 * 1000).toISOString(),
                all_day: false,
                location: '',
                color: '#7986CB'
              };

          // Add Google Meet for videochiamata type
          if (formData.type === 'videochiamata') {
            setCreatingMeet(true);

            // Get current user email to include as attendee
            const currentUserEmail = user?.email;

            // Build allAttendees from selectedAttendees
            const allAttendees = selectedAttendees.map(a => a.email);

            // Add current user (organizer) to attendees list
            // This ensures they appear in the event and receive notifications
            if (currentUserEmail) {
              allAttendees.push(currentUserEmail);
            }

            // Se l'attività è assegnata a qualcun altro, aggiungi quell'utente come attendee
            if (assignedUserId && assignedUserId !== user.id) {
              const { data: assignedUserProfile } = await supabase
                .from('user_profiles')
                .select('email')
                .eq('id', assignedUserId)
                .single();

              if (assignedUserProfile?.email) {
                allAttendees.push(assignedUserProfile.email);
              }
            }

            // Remove duplicates
            const uniqueAttendees = [...new Set(allAttendees)];

            // Add conference data for Google Meet SOLO se il provider scelto è Google Meet
            // (con Teams o Nessuno l'evento Google viene creato senza Meet)
            if (meetingProvider === 'google_meet') {
              eventData.conference_data = {
                createRequest: {
                  requestId: `meet-${Date.now()}`,
                  conferenceSolutionKey: { type: 'hangoutsMeet' }
                }
              };
            }

            // Add attendees in the correct format: array of objects with email property
            eventData.attendees = uniqueAttendees.map(email => ({ email }));
          } else {
            // Per altri tipi di attività, aggiungi comunque l'utente assegnato come attendee se diverso
            if (assignedUserId && assignedUserId !== user.id) {
              const { data: assignedUserProfile } = await supabase
                .from('user_profiles')
                .select('email')
                .eq('id', assignedUserId)
                .single();

              if (assignedUserProfile?.email) {
                if (!eventData.attendees) {
                  eventData.attendees = [];
                }
                eventData.attendees.push({ email: assignedUserProfile.email });
              }
            }
          }

          if (editingActivity && googleEventId) {
            // NOTA: Ora l'evento è sempre nel calendario del creator, non serve più spostarlo
            // Aggiorniamo semplicemente l'evento esistente
            const updatedEvent = await updateEvent(googleEventId, activitiesCalendarId, eventData);
            if (updatedEvent) {
              console.log('Google Calendar event updated successfully');
            }
          } else if (!editingActivity) {
            // Create new Google Calendar event in the organizer's calendar
            const calendarToUse = formData.type === 'videochiamata' ? organizerCalendarId : activitiesCalendarId;
            const createdEvent = await createEvent(calendarToUse || activitiesCalendarId, eventData, organizerId);
            if (createdEvent?.id) {
              newGoogleEventId = createdEvent.id;
              console.log('Google Calendar event created successfully:', newGoogleEventId);

              // Extract Meet link if available
              if (formData.type === 'videochiamata' && createdEvent.conference_data?.entryPoints) {
                const videoEntry = createdEvent.conference_data.entryPoints
                  .find((ep: any) => ep.entryPointType === 'video');
                if (videoEntry?.uri) {
                  meetLink = videoEntry.uri;
                  console.log('Meet link created:', meetLink);
                }
              }
            }
            setCreatingMeet(false);
          }
        } catch (googleError) {
          console.error('Google Calendar sync failed:', googleError);
          setCreatingMeet(false);
          // Don't fail the entire operation, just log the error
          setError('Activity saved, but Google Calendar sync failed');
        }
      } else if (!shouldSyncToGoogleCalendar && googleEventId && activitiesCalendarId) {
        // User disabled sync but event exists - delete from Google Calendar
        try {
          await deleteEvent(googleEventId, activitiesCalendarId);
          newGoogleEventId = null;
          console.log('Google Calendar event deleted successfully');
        } catch (googleError) {
          console.error('Failed to delete Google Calendar event:', googleError);
        }
      }

      // ===== Push Microsoft (Outlook) — INDIPENDENTE dal push Google =====
      // Evento sul calendario dell'ASSEGNATARIO (target_user_id via edge, F1B).
      let newMicrosoftEventId = microsoftEventId;
      const msTargetUserId = assignedUserId || user.id;
      if (assignedMicrosoftConnected && microsoftSyncEnabled && microsoftCalendarId) {
        try {
          const baseEventData = dealData
            ? createEnhancedEventData(formData, dateTime, dealData, dealId, includeDescriptionInEvent)
            : {
                title: formData.title,
                description: formData.description,
                start_time: dateTime.toISOString(),
                end_time: new Date(dateTime.getTime() + 60 * 60 * 1000).toISOString(),
                all_day: false,
                location: ''
              };

          // Attendees come array di email
          const msAttendees: string[] = [];
          if (formData.type === 'videochiamata') {
            msAttendees.push(...selectedAttendees.map(a => a.email));
            if (user?.email) msAttendees.push(user.email);
          }
          if (assignedUserId && assignedUserId !== user.id) {
            const assignedEmail = organizationUsers.find(u => u.id === assignedUserId)?.email;
            if (assignedEmail) msAttendees.push(assignedEmail);
          }

          const msEventData: any = {
            title: baseEventData.title,
            description: baseEventData.description || '',
            start_time: baseEventData.start_time,
            end_time: baseEventData.end_time,
            all_day: baseEventData.all_day || false,
            location: (baseEventData as any).location || '',
            attendees: [...new Set(msAttendees)]
          };

          if ((editingActivity || activityId) && microsoftEventId) {
            // Update evento esistente sul calendario dell'assegnatario (not_found tollerato)
            await updateMicrosoftEvent(microsoftEventId, msEventData, msTargetUserId);
          } else if (!(editingActivity || activityId)) {
            const wantsTeams = meetingProvider === 'microsoft_teams' && formData.type === 'videochiamata';
            if (wantsTeams) setCreatingMeet(true);
            const msResult = await createMicrosoftEvent(microsoftCalendarId, msEventData, {
              createTeamsMeeting: wantsTeams,
              targetUserId: msTargetUserId
            });
            if (msResult?.eventId) {
              newMicrosoftEventId = msResult.eventId;
              if (wantsTeams && msResult.joinUrl && !meetLink) {
                meetLink = msResult.joinUrl;
              }
            }
            if (wantsTeams) setCreatingMeet(false);
          }
        } catch (microsoftError) {
          console.error('Microsoft Calendar sync failed:', microsoftError);
          setCreatingMeet(false);
          // Non bloccare il salvataggio dell'attività
        }
      } else if (!microsoftSyncEnabled && microsoftEventId) {
        // Sync Microsoft disattivato ma evento esistente - elimina da Outlook.
        // La delete passa target_user_id = assegnatario dell'attività (F1B): l'evento
        // vive sul calendario dell'assegnatario, quindi il delete lo colpisce
        // correttamente a prescindere da chi edita (niente più 404-orfano → la
        // guardia creatore T7-R1 non serve più).
        try {
          await deleteMicrosoftEvent(microsoftEventId, msTargetUserId);
          newMicrosoftEventId = null;
          console.log('Microsoft Calendar event deleted successfully');
        } catch (microsoftError) {
          console.error('Failed to delete Microsoft Calendar event:', microsoftError);
        }
      }
      // ===== Fine push Microsoft =====

      if (editingActivity || activityId) {
        // UPDATE existing activity
        const updateData: any = {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          status: formData.status,
          due_date: dateTime.toISOString(),
          assigned_to: assignedTo && assignedTo !== '' ? assignedTo : user.id,
          google_event_id: normalizeGoogleEventId(newGoogleEventId),
          calendar_id: activitiesCalendarId,
          microsoft_event_id: newMicrosoftEventId
        };

        // Add meet_link if available
        if (meetLink) {
          updateData.meet_link = meetLink;
        }

        // Set completed_at when marking as completed
        const previousStatus = editingActivity?.status;
        if (formData.status === 'completed' && previousStatus !== 'completed') {
          updateData.completed_at = new Date().toISOString();
        }

        console.log('[DealActivityForm] Updating activity with data:', {
          activityId: editingActivity?.id || activityId,
          assigned_to: updateData.assigned_to,
          assignedToFromState: assignedTo
        });

        const idToUse = editingActivity?.id || activityId;
        const { error } = await supabase
          .from('sales_activities')
          .update(updateData)
          .eq('id', idToUse);

        if (error) {
          console.error("Error updating activity:", error);
          throw error;
        }

        console.log(`Activity updated successfully: ${idToUse}`);

        // Call onSave if provided instead of onSuccess for calendar integration
        if (onSave) {
          onSave(updateData);
        } else if (onSuccess) {
          onSuccess();
        }
      } else {
        // CREATE new activity
        // Use the organizer's calendar for videochiamata, otherwise use the default
        const savedCalendarId = formData.type === 'videochiamata' ? (organizerCalendarId || activitiesCalendarId) : activitiesCalendarId;
        const activityData: any = {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          status: formData.status,
          due_date: dateTime.toISOString(),
          deal_id: dealId,
          client_id: dealData?.client_id, // Include client_id from deal for better tracking
          user_id: user.id, // Creator of the activity
          assigned_to: assignedTo && assignedTo !== '' ? assignedTo : user.id, // User assigned to complete the activity
          organization_id: organizationId,
          google_event_id: normalizeGoogleEventId(newGoogleEventId),
          calendar_id: savedCalendarId,
          microsoft_event_id: newMicrosoftEventId
        };

        // Add meet_link if available
        if (meetLink) {
          activityData.meet_link = meetLink;
        }

        console.log('[DealActivityForm] Creating new activity with data:', {
          assigned_to: activityData.assigned_to,
          assignedToFromState: assignedTo
        });

        // Add user_id if the column exists
        if (hasUserProfileColumn) {
          activityData.user_id = user.id;
        }

        console.log(`Inserting activity with ${hasUserProfileColumn ? 'user_id' : 'only user_id'}`);

        const { data, error } = await supabase
          .from('sales_activities')
          .insert(activityData)
          .select(); // Get the inserted data back

        if (error) {
          console.error("Error inserting activity:", error);
          throw error;
        }

        console.log(`Activity created successfully: ${data?.[0]?.id}`);

        // Emit event for workflow triggers
        try {
          // Check if this deal is associated with a lead
          const { data: dealData } = await supabase
            .from('deals')
            .select('lead_id, client_id, title')
            .eq('id', dealId)
            .maybeSingle();

          // Always emit deal:activity_created for deal workflows
          const { eventBus } = await import('../../lib/events/eventBus');
          console.log('🔄 [DealActivityForm] Emitting deal:activity_created event');
          eventBus.emitEvent({
            type: 'deal:activity_created',
            payload: {
              dealId: dealId,
              activityId: data[0].id,
              activityType: activityData.type,
              title: activityData.title,
              clientId: dealData?.client_id,
              leadId: dealData?.lead_id,
              assignedTo: activityData.assigned_to,
              dueDate: activityData.due_date,
            }
          });

          if (dealData?.lead_id) {
            // Get lead info to emit event
            const { data: leadData } = await supabase
              .from('clients')
              .select('business_name, pipeline_id, stato_cliente')
              .eq('id', dealData.lead_id)
              .maybeSingle();

            // Check if it's actually a lead
            if (leadData) {
              const { data: stateData } = await supabase
                .from('client_states')
                .select('is_lead')
                .eq('id', leadData.stato_cliente)
                .maybeSingle();

              if (stateData?.is_lead) {
                // Emit lead:activity_created event
                console.log('🔄 [DealActivityForm] Emitting lead:activity_created event');
                eventBus.emitEvent({
                  type: 'lead:activity_created',
                  payload: {
                    leadId: dealData.lead_id,
                    activityId: data[0].id,
                    activityType: activityData.type,
                    pipelineId: leadData.pipeline_id,
                    businessName: leadData.business_name,
                    activityTitle: activityData.title,
                    dueDate: activityData.due_date,
                    assignedTo: activityData.assigned_to,
                  }
                });
              }
            }
          } else if (dealData?.client_id) {
            // Get client info to emit event
            const { data: clientData } = await supabase
              .from('clients')
              .select('business_name, stato_cliente')
              .eq('id', dealData.client_id)
              .maybeSingle();

            if (clientData) {
              const { data: stateData } = await supabase
                .from('client_states')
                .select('is_lead')
                .eq('id', clientData.stato_cliente)
                .maybeSingle();

              if (!stateData?.is_lead) {
                // Emit client:activity_created event
                console.log('🔄 [DealActivityForm] Emitting client:activity_created event');
                eventBus.emitEvent({
                  type: 'client:activity_created',
                  payload: {
                    clientId: dealData.client_id,
                    activityId: data[0].id,
                    activityType: activityData.type,
                    businessName: clientData.business_name,
                    activityTitle: activityData.title,
                    dueDate: activityData.due_date,
                    assignedTo: activityData.assigned_to,
                  }
                });
              }
            }
          }
        } catch (eventError) {
          console.error('Error emitting activity event:', eventError);
          // Don't fail activity creation if event fails
        }

        // Call onSave if provided instead of onSuccess for calendar integration
        if (onSave) {
          onSave(activityData);
        } else if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      console.error('Error creating activity:', err);
      setError('Failed to create activity');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4" />;
      case 'videochiamata': return <Video className="h-4 w-4" />;
      case 'appuntamento': return <CalendarCheck className="h-4 w-4" />;
      case 'sopralluogo': return <MapPin className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'task': return <ClipboardList className="h-4 w-4" />;
      case 'note': return <FileText className="h-4 w-4" />;
      default:
        if (type.startsWith('alfadocs_')) return <Stethoscope className="h-4 w-4" />;
        return <CalendarClock className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'overdue': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-blue-500" />;
    }
  };

  return (
    <div className="p-5">
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className={`block text-sm font-medium mb-1 ${titleError ? 'text-red-500' : 'text-black/80 dark:text-white/80'}`}>
              {t.activities.title}{activityTitleRequired ? ' *' : ''}
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={(e) => {
                handleChange(e);
                if (titleError) setTitleError(false);
              }}
              className={`w-full px-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:border-transparent ${
                titleError
                  ? 'ring-2 ring-red-500 border-red-500'
                  : 'focus:ring-black dark:focus:ring-white'
              }`}
              placeholder={t.activities.activityTitle}
            />
            {titleError && (
              <p className="mt-1 text-xs text-red-500">Il titolo è obbligatorio</p>
            )}
          </div>
          
          {/* Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-black/80 dark:text-white/80 mb-1">
              {t.activities.type}
            </label>
            <div className="relative">
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent appearance-none"
              >
                <option value="call">{t.activities.call}</option>
                <option value="videochiamata">Videochiamata</option>
                <option value="appuntamento">Appuntamento fisico</option>
                <option value="sopralluogo">Sopralluogo</option>
                <option value="email">{t.activities.email}</option>
                <option value="task">{t.activities.task}</option>
                <option value="other">{t.activities.other}</option>
                {alfadocsActive && alfadocsTypes.length > 0 && (
                  <>
                    <option disabled>──── {alfadocsLabel} ────</option>
                    {alfadocsTypes.map((type: any) => (
                      <option key={`alfadocs_${type.value}`} value={`alfadocs_${type.value}`}>
                        {type.label}
                      </option>
                    ))}
                  </>
                )}
                {customTypeOptions.length > 0 && (
                  <>
                    <option disabled>──── Tipi personalizzati ────</option>
                    {customTypeOptions.map(customType => (
                      <option key={customType.slug} value={customType.slug}>
                        {customType.active ? customType.name : `${customType.name} (disattivato)`}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-black/60 dark:text-white/60">
                {getTypeIcon(formData.type)}
              </div>
            </div>
          </div>
          
          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-black/80 dark:text-white/80 mb-1">
              {t.activities.status}
            </label>
            <div className="relative">
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent appearance-none"
              >
                <option value="pending">{t.activities.pending}</option>
                <option value="completed">{t.activities.completed}</option>
                <option value="overdue">{t.activities.overdue}</option>
              </select>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {getStatusIcon(formData.status)}
              </div>
            </div>
          </div>
          
          {/* Assigned User */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <label htmlFor="assignedTo" className="block text-sm font-medium text-black/80 dark:text-white/80">
                Nel calendario di
              </label>
              <HelpTip title="Nel calendario di" text="Lo trova questa persona nel suo calendario. Tu resti come chi l'ha fissato." />
            </div>
            <div className="relative">
              <select
                id="assignedTo"
                name="assignedTo"
                value={assignedTo || ''}
                onChange={(e) => {
                  console.log('[DealActivityForm] Assignee changed:', {
                    previousValue: assignedTo,
                    newValue: e.target.value,
                    newValueType: typeof e.target.value,
                    newValueIsEmpty: e.target.value === ''
                  });
                  setAssignedTo(e.target.value);
                }}
                className="w-full pl-10 pr-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent appearance-none"
              >
                <option value="">{t.activities.selectUser || "Select User"}</option>
                {organizationUsers.map(orgUser => (
                  <option key={orgUser.id} value={orgUser.id}>
                    {orgUser.full_name || orgUser.email}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-4 w-4 text-black/60 dark:text-white/60" />
              </div>
            </div>
          </div>

          {/* Selettore provider meeting - Solo per videochiamata, se almeno un provider disponibile */}
          {formData.type === 'videochiamata' && (assignedGoogleConnected || (assignedMicrosoftConnected && teamsServiceEnabled)) && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-600">Provider meeting</label>
              <select
                value={meetingProvider}
                onChange={(e) => setMeetingProvider(e.target.value as 'none' | 'google_meet' | 'microsoft_teams')}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
              >
                <option value="none">Nessuno</option>
                {assignedGoogleConnected && <option value="google_meet">Google Meet</option>}
                {assignedMicrosoftConnected && teamsServiceEnabled && (
                  <option value="microsoft_teams" disabled={!microsoftSyncEnabled}>
                    Microsoft Teams
                  </option>
                )}
              </select>
              {assignedMicrosoftConnected && teamsServiceEnabled && !microsoftSyncEnabled && (
                <p className="text-xs text-amber-600">
                  Attiva la sincronizzazione attività sul calendario Microsoft nelle impostazioni per usare Teams.
                </p>
              )}
              {meetingProvider === 'microsoft_teams' && (
                <p className="text-xs text-gray-500">
                  Il link Teams non può essere rimosso dall'evento dopo la creazione.
                </p>
              )}
            </div>
          )}

          {/* Google Meet Attendees - Only for videochiamata type */}
          {formData.type === 'videochiamata' && (
            <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-2 space-y-2">
              {/* Meet Organizer Selection - show when commercial is different from agent */}
              {(() => {
                const salesRepId = dealData?.clients?.sales_rep_id;
                const hasDifferentCommercial = salesRepId && salesRepId !== assignedTo;
                const agentHasGoogle = organizationUsers.find(u => u.id === assignedTo)
                  ?.google_workspace_scopes?.includes('https://www.googleapis.com/auth/calendar');
                const commercialHasGoogle = salesRepId && organizationUsers.find(u => u.id === salesRepId)
                  ?.google_workspace_scopes?.includes('https://www.googleapis.com/auth/calendar');

                if (hasDifferentCommercial) {
                  return (
                    <div className="flex items-center gap-2 text-xs pb-2 border-b border-blue-200">
                      <span className="font-medium text-gray-700 whitespace-nowrap">Organizzatore meet:</span>
                      <div className="inline-flex rounded border border-gray-300 p-0.5 bg-white">
                        <button
                          type="button"
                          onClick={() => agentHasGoogle && setMeetOrganizer('agent')}
                          disabled={!agentHasGoogle}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            meetOrganizer === 'agent' && agentHasGoogle
                              ? 'bg-[#0f6cbd] text-white'
                              : agentHasGoogle
                                ? 'text-gray-700 hover:bg-gray-100'
                                : 'text-gray-400 cursor-not-allowed bg-gray-50'
                          }`}
                          title={!agentHasGoogle ? 'Google Calendar non connesso' : ''}
                        >
                          Agente{!agentHasGoogle && ' (no GCal)'}
                        </button>
                        <button
                          type="button"
                          onClick={() => commercialHasGoogle && setMeetOrganizer('commercial')}
                          disabled={!commercialHasGoogle}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            meetOrganizer === 'commercial' && commercialHasGoogle
                              ? 'bg-[#0f6cbd] text-white'
                              : commercialHasGoogle
                                ? 'text-gray-700 hover:bg-gray-100'
                                : 'text-gray-400 cursor-not-allowed bg-gray-50'
                          }`}
                          title={!commercialHasGoogle ? 'Google Calendar non connesso' : ''}
                        >
                          Commerciale{!commercialHasGoogle && ' (no GCal)'}
                        </button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Invitati - compact layout */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Invitati:</span>
                {organizationUsers.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const userId = e.target.value;
                      if (!userId) return;
                      const selectedUser = organizationUsers.find(u => u.id === userId);
                      if (selectedUser && !selectedAttendees.some(a => a.email === selectedUser.email)) {
                        setSelectedAttendees([
                          ...selectedAttendees,
                          {
                            email: selectedUser.email,
                            name: selectedUser.full_name || undefined,
                            source: 'crm_user'
                          }
                        ]);
                      }
                    }}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                  >
                    <option value="">Aggiungi collega...</option>
                    {organizationUsers
                      .filter(u => u.email && !selectedAttendees.some(a => a.email === u.email))
                      .map(u => (
                        <option key={u.id} value={u.id}>
                          {u.full_name || u.email}{u.id === dealData?.clients?.sales_rep_id ? ' (Commerciale)' : ''}
                        </option>
                      ))
                    }
                  </select>
                )}
              </div>

              {/* Contact selection - compact chips */}
              {availableContacts.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {availableContacts.map((contact) => {
                    const isSelected = selectedAttendees.some(a => a.email === contact.email);
                    return (
                      <label
                        key={contact.email}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAttendees([...selectedAttendees, contact]);
                            } else {
                              setSelectedAttendees(selectedAttendees.filter(a => a.email !== contact.email));
                            }
                          }}
                          className="h-3 w-3 text-blue-600 rounded"
                        />
                        <span className="truncate max-w-[150px]">{contact.name || contact.email}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Add manual email - inline */}
              <div className="flex gap-1">
                <input
                  type="email"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder="Email manuale..."
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const email = manualEmail.trim();
                      if (email && email.includes('@') && !selectedAttendees.some(a => a.email === email)) {
                        setSelectedAttendees([...selectedAttendees, { email, source: 'manual' }]);
                        setManualEmail('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const email = manualEmail.trim();
                    if (email && email.includes('@') && !selectedAttendees.some(a => a.email === email)) {
                      setSelectedAttendees([...selectedAttendees, { email, source: 'manual' }]);
                      setManualEmail('');
                    }
                  }}
                  className="px-2 py-1 text-xs bg-[#0f6cbd] text-white rounded hover:bg-[#0a5ba8]"
                >
                  +
                </button>
              </div>

              {/* Selected attendees - compact tags */}
              {selectedAttendees.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1 border-t border-blue-200">
                  <span className="text-xs text-gray-500">({selectedAttendees.length}):</span>
                  {selectedAttendees.map((attendee) => (
                    <span
                      key={attendee.email}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs"
                    >
                      <span className="truncate max-w-[120px]">{attendee.name || attendee.email}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedAttendees(selectedAttendees.filter(a => a.email !== attendee.email))}
                        className="text-blue-600 hover:text-blue-800 font-bold"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Meet creation indicator */}
          {creatingMeet && (
            <div className="flex items-center text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
              Creazione meet in corso...
            </div>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="due_date" className="block text-sm font-medium text-black/80 dark:text-white/80 mb-1">
                {t.activities.date}
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-4 w-4 text-black/60 dark:text-white/60" />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="due_time" className="block text-sm font-medium text-black/80 dark:text-white/80 mb-1">
                {t.activities.time}
              </label>
              <div className="relative">
                <input
                  type="time"
                  id="due_time"
                  name="due_time"
                  value={formData.due_time}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-4 w-4 text-black/60 dark:text-white/60" />
                </div>
              </div>
            </div>
          </div>
          
          <AssigneeAvailabilityHint
            assigneeId={assignedTo}
            assigneeName={organizationUsers.find(u => u.id === assignedTo)?.full_name || organizationUsers.find(u => u.id === assignedTo)?.email}
            date={formData.due_date}
            time={formData.due_time}
            excludeActivityId={activityId || editingActivity?.id}
            onUseTime={(hhmm) => setFormData(prev => ({ ...prev, due_time: hhmm }))}
          />

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-black/80 dark:text-white/80 mb-1">
              {t.activities.description}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 glass-card rounded-lg text-black/80 dark:text-white/80 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
              placeholder={t.activities.descriptionPlaceholder}
            ></textarea>
          </div>

          {/* Toggle for including description in Google Calendar event */}
          {['videochiamata', 'appuntamento', 'sopralluogo'].includes(formData.type) && (
            <div className="flex items-center justify-between p-3 glass-card border border-black/10 dark:border-white/10 rounded-lg">
              <div>
                <label htmlFor="include-description-toggle" className="text-sm font-medium text-black/80 dark:text-white/80">
                  Mostra descrizione nell'evento Google Calendar
                </label>
                <p className="text-xs text-black/60 dark:text-white/60 mt-0.5">
                  Includi la descrizione dell'attività nell'evento del calendario
                </p>
              </div>
              <button
                type="button"
                id="include-description-toggle"
                role="switch"
                aria-checked={includeDescriptionInEvent}
                onClick={() => setIncludeDescriptionInEvent(prev => !prev)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  includeDescriptionInEvent ? 'bg-[#0f6cbd]' : 'bg-gray-300'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    includeDescriptionInEvent ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Google Calendar Sync Status - Hidden per request */}
          {/* {shouldSyncToGoogleCalendar && activitiesCalendarId && (
            <div className="glass-card rounded-lg border border-green-200 p-3 bg-green-50/50">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-green-600" />
                <span className="font-medium text-sm text-green-800">Sincronizzazione Google Calendar Abilitata</span>
              </div>
              <p className="text-xs text-green-700 mt-1">
                Questa attività sarà automaticamente sincronizzata con il calendario Google selezionato
              </p>
              {googleEventId && (
                <div className="mt-2 flex items-center gap-2 text-xs text-green-700">
                  <CheckCircle className="h-3 w-3" />
                  <span>Già sincronizzato con Google Calendar</span>
                </div>
              )}
            </div>
          )}

          {shouldSyncToGoogleCalendar && !activitiesCalendarId && (
            <div className="glass-card rounded-lg border border-yellow-200 p-3 bg-yellow-50/50">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <span className="font-medium text-sm text-yellow-800">Calendario non selezionato</span>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                La sincronizzazione è abilitata ma non hai selezionato un calendario per le sales activities. Vai nelle Impostazioni Google Workspace per selezionare un calendario.
              </p>
            </div>
          )} */}
        </div>
        
        {/* Footer with actions */}
        <div className="border-t border-black/10 dark:border-white/10 mt-6 pt-4">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium glass-card rounded-lg text-black/80 dark:text-white/80 hover:bg-black/5 dark:hover:bg-white/5"
            >
              {t.statusUpdate?.cancel || "Cancel"}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium border border-transparent rounded-lg ${
                loading
                  ? 'bg-black/50 dark:bg-gray-800/50 text-white dark:text-black cursor-not-allowed'
                  : 'bg-black dark:bg-white text-white dark:text-black hover:bg-black/80 dark:hover:bg-white/80'
              }`}
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {t.activities.saving}
                </>
              ) : (
                t.activities.saveActivity
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}